import * as pc from 'playcanvas';
import { playSound } from './audio.js';
import { screenShake } from './camera.js';
import { checkAttackHitBoss, checkAttackHitMinion, constrainToArenaBounds } from './collision.js';
import { stunBoss, getBossState } from './ai.js';
import { createGlowMaterial } from '../entities/materials.js';

// Damage a player
export function damagePlayer(player, amount) {
    if (player.isInvincible || player.health <= 0) return false;

    player.health -= amount;
    player.isInvincible = true;
    player.invincibleTimer = 0.7;

    screenShake(0.2);
    flashScreen();
    playSound('damage');

    console.log(`Player ${player.playerNumber} took ${amount} damage! Health: ${player.health}`);

    if (player.health <= 0) {
        player.health = 0;
        // Create defeat effects before hiding
        const playerPos = player.getPosition();
        createDefeatExplosion(player._app || window.app, playerPos, player.playerNumber);
        showPlayerDefeatedMessage(player.playerNumber);
        // Hide the defeated player
        player.enabled = false;
        console.log(`Player ${player.playerNumber} defeated!`);
        return true; // Player defeated
    }

    return false;
}

// Update player invincibility
export function updateInvincibility(player, dt) {
    if (!player.isInvincible) return;

    player.invincibleTimer -= dt;

    // Flash player while invincible
    const visible = Math.floor(player.invincibleTimer * 10) % 2 === 0;
    player.enabled = visible;

    if (player.invincibleTimer <= 0) {
        player.isInvincible = false;
        // Only re-enable if player is still alive
        if (player.health > 0) {
            player.enabled = true;
        }
    }
}

// Player attack action
export function performAttack(app, player, boss, minions, callback) {
    if (player.isAttacking || player.health <= 0) return;

    player.isAttacking = true;
    playSound('attack');

    const playerPos = player.getPosition();

    // Lunge forward
    const lungeDir = player.forward.clone();
    player.setPosition(
        playerPos.x + lungeDir.x * 0.5,
        playerPos.y,
        playerPos.z + lungeDir.z * 0.5
    );

    // Check boss hit
    const bossHit = checkAttackHitBoss(player, boss);
    if (bossHit.hit) {
        const { state } = getBossState();
        const damage = (state === 'stunned' || state === 'recovering') ? 15 : 8;
        boss.health -= damage;

        // Stun the boss
        stunBoss(boss, 0.5);

        // Knockback
        const knockbackForce = 2;
        const bossPos = boss.getPosition();
        const newBossX = bossPos.x + (bossHit.dx / bossHit.distance) * knockbackForce;
        const newBossZ = bossPos.z + (bossHit.dz / bossHit.distance) * knockbackForce;
        const constrainedBoss = constrainToArenaBounds(newBossX, newBossZ);
        boss.setPosition(constrainedBoss.x, bossPos.y, constrainedBoss.z);

        screenShake(0.15);
        createHitParticles(app, bossPos);
        playSound('hit');
        console.log(`Boss took ${damage} damage! Health: ${boss.health}`);

        if (boss.health <= 0) {
            boss.health = 0;
            if (callback) callback('victory');
        }
    }

    // Check minion hits
    const attackPos = player.getPosition();
    for (const minion of minions) {
        if (minion.health <= 0) continue;

        const minionHit = checkAttackHitMinion(player, minion);
        if (minionHit.hit) {
            const minionDamage = 15;
            minion.health -= minionDamage;
            minion.stunTimer = 0.3;

            // Knockback
            if (minionHit.distance > 0) {
                const minionPos = minion.getPosition();
                minion.setPosition(
                    minionPos.x + (minionHit.dx / minionHit.distance) * 1.5,
                    minionPos.y,
                    minionPos.z + (minionHit.dz / minionHit.distance) * 1.5
                );
            }

            createHitParticles(app, minion.getPosition());
            playSound('hit');
        }
    }

    // Attack animation
    player.setLocalScale(
        player.baseScale.x * 1.2,
        player.baseScale.y * 1.15,
        player.baseScale.z * 0.7
    );

    setTimeout(() => {
        if (player && !player.destroyed) {
            player.setLocalScale(player.baseScale.x, player.baseScale.y, player.baseScale.z);
            player.isAttacking = false;
        }
    }, 150);
}

// Player dodge action
export function performDodge(player, boss) {
    if (player.isDodging || player.health <= 0) return;

    playSound('dodge');
    player.isDodging = true;
    player.isInvincible = true;
    player.invincibleTimer = 0.4;

    const playerPos = player.getPosition();
    const bossPos = boss.getPosition();

    // Dash away from boss
    const dx = playerPos.x - bossPos.x;
    const dz = playerPos.z - bossPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    const dashDistance = 3;
    const targetX = playerPos.x + (dx / dist) * dashDistance;
    const targetZ = playerPos.z + (dz / dist) * dashDistance;
    const constrainedDodge = constrainToArenaBounds(targetX, targetZ);

    player.setPosition(constrainedDodge.x, playerPos.y, constrainedDodge.z);

    // Squash animation
    player.setLocalScale(
        player.baseScale.x * 1.3,
        player.baseScale.y * 0.7,
        player.baseScale.z * 1.3
    );

    // Create trail effect
    createDodgeTrail(player._app || window.app, playerPos);

    setTimeout(() => {
        if (player && !player.destroyed) {
            player.setLocalScale(player.baseScale.x, player.baseScale.y, player.baseScale.z);
            player.isDodging = false;
        }
    }, 200);
}

// Create hit particles
export function createHitParticles(app, position) {
    const colors = [
        new pc.Color(1, 0.3, 0.1),
        new pc.Color(1, 0.8, 0.2),
        new pc.Color(1, 1, 1),
        new pc.Color(0.8, 0.2, 0.5)
    ];

    for (let i = 0; i < 8; i++) {
        const particle = new pc.Entity('Particle');
        const color = colors[Math.floor(Math.random() * colors.length)];
        particle.addComponent('render', {
            type: 'sphere',
            material: createGlowMaterial(color, 1.2)
        });

        particle.setPosition(
            position.x + (Math.random() - 0.5) * 1.5,
            position.y + Math.random() * 1.5,
            position.z + (Math.random() - 0.5) * 1.5
        );
        particle.setLocalScale(0.25, 0.25, 0.25);
        app.root.addChild(particle);

        let life = 0.4;
        const vel = {
            x: (Math.random() - 0.5) * 8,
            y: Math.random() * 6 + 3,
            z: (Math.random() - 0.5) * 8
        };

        const updateParticle = setInterval(() => {
            life -= 0.016;
            if (life <= 0) {
                clearInterval(updateParticle);
                particle.destroy();
                return;
            }

            const pos = particle.getPosition();
            particle.setPosition(
                pos.x + vel.x * 0.016,
                pos.y + vel.y * 0.016,
                pos.z + vel.z * 0.016
            );
            vel.y -= 20 * 0.016;

            const scale = life * 0.6;
            particle.setLocalScale(scale, scale, scale);
        }, 16);
    }
}

// Create dodge trail effect
function createDodgeTrail(app, position) {
    const trail = new pc.Entity('Trail');
    trail.addComponent('render', {
        type: 'sphere',
        material: createGlowMaterial(new pc.Color(1, 0.9, 0.3), 0.8)
    });
    trail.setPosition(position.x, position.y, position.z);
    trail.setLocalScale(1, 1.1, 0.9);
    app.root.addChild(trail);

    let life = 0.3;
    const fadeInterval = setInterval(() => {
        life -= 0.02;
        if (life <= 0) {
            clearInterval(fadeInterval);
            trail.destroy();
            return;
        }
        const scale = life * 3;
        trail.setLocalScale(scale, scale * 1.1, scale * 0.9);
    }, 16);
}

// Flash the screen (damage feedback)
export function flashScreen() {
    const flash = document.createElement('div');
    flash.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.3);
        z-index: 9999;
        pointer-events: none;
        animation: flash 0.2s ease;
    `;
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), 200);
}

// Show player defeated message
function showPlayerDefeatedMessage(playerNumber) {
    const color = playerNumber === 1 ? '#ff6b6b' : '#4dabf7';
    const heart = playerNumber === 1 ? '💔' : '💙';

    const msg = document.createElement('div');
    msg.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        background: linear-gradient(135deg, ${color}, #2d2d2d);
        color: white;
        padding: 20px 40px;
        border-radius: 15px;
        font-size: 28px;
        font-weight: bold;
        z-index: 10000;
        text-align: center;
        border: 3px solid ${color};
        box-shadow: 0 0 30px ${color}80;
        animation: defeatPop 0.5s ease forwards;
    `;
    msg.innerHTML = `${heart} PLAYER ${playerNumber} DEFEATED!`;

    // Add animation keyframes if not present
    if (!document.getElementById('defeat-animation-style')) {
        const style = document.createElement('style');
        style.id = 'defeat-animation-style';
        style.textContent = `
            @keyframes defeatPop {
                0% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
                50% { transform: translate(-50%, -50%) scale(1.2); }
                100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            }
            @keyframes defeatFade {
                0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8) translateY(-20px); }
            }
        `;
        document.head.appendChild(style);
    }

    document.body.appendChild(msg);

    // Mark health bar as defeated
    markHealthBarDefeated(playerNumber);

    // Fade out after delay
    setTimeout(() => {
        msg.style.animation = 'defeatFade 0.5s ease forwards';
        setTimeout(() => msg.remove(), 500);
    }, 2000);
}

// Mark health bar as defeated
function markHealthBarDefeated(playerNumber) {
    const healthBarId = playerNumber === 1 ? 'playerHealthBar' : `player${playerNumber}HealthBar`;
    const healthBar = document.getElementById(healthBarId);

    if (healthBar) {
        const container = healthBar.parentElement;
        if (container && !container.querySelector('.defeated-overlay')) {
            const overlay = document.createElement('div');
            overlay.className = 'defeated-overlay';
            overlay.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(0, 0, 0, 0.7);
                color: #ff6b6b;
                font-size: 12px;
                font-weight: bold;
                letter-spacing: 1px;
                border-radius: inherit;
            `;
            overlay.textContent = 'DEFEATED';
            container.style.position = 'relative';
            container.appendChild(overlay);
        }
    }
}

// Create defeat explosion particles
function createDefeatExplosion(app, position, playerNumber) {
    if (!app) return;

    const baseColor = playerNumber === 1
        ? new pc.Color(1, 0.4, 0.4)  // Red for P1
        : new pc.Color(0.3, 0.5, 1); // Blue for P2

    // Create a burst of particles
    for (let i = 0; i < 20; i++) {
        const particle = new pc.Entity('DefeatParticle');

        // Vary the color slightly
        const colorVariation = 0.2;
        const color = new pc.Color(
            Math.min(1, baseColor.r + (Math.random() - 0.5) * colorVariation),
            Math.min(1, baseColor.g + (Math.random() - 0.5) * colorVariation),
            Math.min(1, baseColor.b + (Math.random() - 0.5) * colorVariation)
        );

        particle.addComponent('render', {
            type: 'sphere',
            material: createGlowMaterial(color, 1.5)
        });

        // Start at player position
        particle.setPosition(
            position.x + (Math.random() - 0.5) * 0.5,
            position.y + Math.random() * 0.5,
            position.z + (Math.random() - 0.5) * 0.5
        );

        const size = 0.15 + Math.random() * 0.2;
        particle.setLocalScale(size, size, size);
        app.root.addChild(particle);

        // Explosion velocity - radial outward
        const angle = (i / 20) * Math.PI * 2;
        const speed = 5 + Math.random() * 5;
        const vel = {
            x: Math.cos(angle) * speed + (Math.random() - 0.5) * 3,
            y: 5 + Math.random() * 8,
            z: Math.sin(angle) * speed + (Math.random() - 0.5) * 3
        };

        let life = 0.8 + Math.random() * 0.4;
        const maxLife = life;

        const updateParticle = setInterval(() => {
            life -= 0.016;
            if (life <= 0) {
                clearInterval(updateParticle);
                particle.destroy();
                return;
            }

            const pos = particle.getPosition();
            particle.setPosition(
                pos.x + vel.x * 0.016,
                pos.y + vel.y * 0.016,
                pos.z + vel.z * 0.016
            );
            vel.y -= 15 * 0.016; // Gravity

            // Fade and shrink
            const lifeRatio = life / maxLife;
            const scale = size * lifeRatio;
            particle.setLocalScale(scale, scale, scale);
        }, 16);
    }

    // Extra screen shake for defeat
    screenShake(0.4);
}
